import { ReactNode } from "react";
import * as React from "react";
import cn from "classnames";
import { v4 as uuidv4 } from "uuid";
import { createBemBlockBuilder } from "@ebsco-ui/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faRedo } from "@fortawesome/pro-regular-svg-icons";
import Button from "@ebsco-ui/button";
import DefaultImage from "./default-image";

import "./page-error-message.scss";

export const IMAGE_PLACEMENT = {
  blockStart: "block-start",
  blockEnd: "block-end",
};

const DEFAULT_TITLE_TAG = "h2";

export interface PageErrorMessageProps {
  buttonIcon?: IconProp | null;
  buttonText?: string;
  className?: string;
  image?: ReactNode;
  imagePlacement?: "block-start" | "block-end";
  subTitle?: string;
  title: string;
  titleTag?: keyof JSX.IntrinsicElements;
  text?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

const BASE_CLASS_NAME = "eb-page-error-message";

const PageErrorMessage = (props: PageErrorMessageProps) => {
  const {
    titleTag,
    buttonIcon = faRedo,
    buttonText = "",
    className: consumerBaseClassName,
    image = null,
    imagePlacement = IMAGE_PLACEMENT.blockStart,
    onClick = null,
    subTitle = "",
    text = "",
    title,
  } = props;

  // We need unique IDs to support screen readers in rare case
  // of having multiple PageErrorMessages on the same page
  const uniquePart = uuidv4();
  const titleId = `title-${uniquePart}`;
  const subTitleId = `sub-title-${uniquePart}`;
  const textId = `text-${uniquePart}`;

  const TitleTag = titleTag || DEFAULT_TITLE_TAG;

  const getBEMBlocksWith = createBemBlockBuilder([
    BASE_CLASS_NAME,
    consumerBaseClassName,
  ]);

  const renderImage = (position: string) => {
    return (
      <div
        className={cn(
          getBEMBlocksWith("__image"),
          getBEMBlocksWith(`__image--${position}`),
        )}
        aria-hidden="true"
      >
        {image || <DefaultImage />}
      </div>
    );
  };

  return (
    <div
      role="alert"
      aria-labelledby={`${titleId} ${subTitleId}`}
      aria-describedby={textId}
      className={getBEMBlocksWith()}
    >
      {imagePlacement === IMAGE_PLACEMENT.blockStart &&
        renderImage(imagePlacement)}
      <TitleTag id={titleId} className={getBEMBlocksWith("__title")}>
        {title}
      </TitleTag>
      {subTitle && (
        <p id={subTitleId} className={getBEMBlocksWith("__sub-title")}>
          {subTitle}
        </p>
      )}
      {text && (
        <p id={textId} className={getBEMBlocksWith("__text")}>
          {text}
        </p>
      )}
      {buttonText && onClick && (
        <Button className={getBEMBlocksWith("__button")} onClick={onClick}>
          {buttonIcon && (
            <FontAwesomeIcon
              icon={buttonIcon}
              className={getBEMBlocksWith("__button-icon")}
              aria-hidden="true"
            />
          )}
          {buttonText}
        </Button>
      )}
      {imagePlacement === IMAGE_PLACEMENT.blockEnd &&
        renderImage(imagePlacement)}
    </div>
  );
};

export default PageErrorMessage;
